import React, { useEffect } from "react";
import { useFormikContext } from "formik";
import ReactTooltip from "react-tooltip";

import { AddressFieldset } from "../../forms/index.jsx";
import AddressBook from "./AddressBook.jsx";
import PendingAddresses from "./PendingAddresses.jsx";
import { FieldGroup } from "../../components";
import DataHelper from "../../helpers/Data";
import { Window } from "../../contexts";
import useCategories from "../../hooks/useCategories.js";

const GENDER_OPTIONS = [
  { id: "men", name: "Male" },
  { id: "women", name: "Female" },
];

const OverviewForm = ({
  user,
  setType,
  isProfile,
  defaultType,
  isRC,
  showAvatar = false,
  // Only supplied while CREATING — held by the Wizard so it can write them once
  // the user id exists. Absent everywhere else, where the address book saves
  // directly.
  extraAddresses,
  setExtraAddresses,
  /**
   * Which HALF of the create form to render.
   *
   * Creating a contact mid-flow — ASSIGN CLIENT on a request, a supplier on a
   * listing — put 29 fields in front of somebody who had four to give. Only
   * first_name, last_name and type are required (plus gender outside RC), so
   * "short" is those plus the one way to reach them, and "rest" is everything
   * else, folded (Alex, 2026-10-27).
   *
   * `null` keeps the whole create form in one piece, and every editing and
   * profile path is untouched — they never pass this.
   */
  part = null,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const showShort = part === null || part === 'short';
  const showRest = part === null || part === 'rest';

  useEffect(() => {
    setType(values.type);
  }, [values.type]);

  useEffect(() => {
    if (defaultType) {
      setFieldValue("type", defaultType);
    }
  }, [defaultType]);

  const isClaimed = user?.email_verified_at;
  const categoryOptions = useCategories();

  if (showAvatar) {
    return (
      <div className="overview-form overview-form--create">
        <div className="overview-form__fields overview-form__fields--create">
          {showShort && (
          <>
          {/* Two columns: avatar | user type, then gender. One column on
              phones, in this DOM order. Contact owner USED to sit here on
              create; it moved into More details with the rest of the record,
              since the four fields above it are the ones you must give. */}
          <div className="overview-form__head">
            <div className="overview-form__avatar">
              <FieldGroup name="avatar_file_id" type="avatar" />
            </div>
            <div className="overview-form__head-type">
              <FieldGroup
                bare
                placeholder="User type"
                type="select"
                options={DataHelper.userType}
                name="type"
                disabled={isProfile}
              />
            </div>
            <div>
              <FieldGroup
                bare
                placeholder="Gender"
                name="gender"
                type="select"
                options={GENDER_OPTIONS}
                disabled={isRC}
                required={!isRC}
              />
            </div>
          </div>

          <div className="row overview-form__row">
            <div className="col-md-6">
              <FieldGroup bare placeholder="First Name" name="first_name" />
            </div>
            <div className="col-md-6">
              <FieldGroup bare placeholder="Last Name" name="last_name" />
            </div>
          </div>

          <div className="row overview-form__row">
            <div className="col-md-6">
              <FieldGroup
                bare
                placeholder="Mobile"
                type="phone"
                name="phone"
                tooltip="Mobile"
                rcCountrySelect
              />
            </div>
            <div className="col-md-6">
              <FieldGroup
                bare
                placeholder="Primary Email"
                type="email"
                name="email"
                readOnly={isClaimed}
                disabled={isClaimed}
              />
            </div>
          </div>
          <ReactTooltip />
          </>
          )}

          {showRest && (
          <>
          <div className="row overview-form__row">
            <div className="col-md-6">
              <FieldGroup
                bare
                placeholder="Contact owner"
                type="select"
                name="owner_id"
                optionsUrl={`/api/users/sales-admins`}
                optionsMethod="post"
                disabled={isRC}
              />
            </div>
            <div className="col-md-6">
              <FieldGroup
                bare
                placeholder="Other Mobile"
                type="phone"
                name="other_phone"
                tooltip="Mobile"
                disabled={isRC}
                rcCountrySelect
              />
            </div>
          </div>
          {/* Other Mobile carries a tooltip, and the provider has to be in the
              half that renders it. */}
          <ReactTooltip />

          <div className="row overview-form__row">
            <div className="col-md-6">
              <FieldGroup
                bare
                placeholder="Date of birth (DD/MM/YYYY)"
                name="birth_date"
                type="masked"
                mask="99/99/9999"
                disabled={isRC}
              />
            </div>
            <div className="col-md-6">
              <FieldGroup
                bare
                placeholder="Nationality"
                name="nationality_id"
                type="nationality"
                disabled={isRC}
              />
            </div>
          </div>

          <div className="row overview-form__row">
            <div className="col-md-6">
              <FieldGroup
                bare
                placeholder="Secondary Email"
                type="email"
                name="secondary_email"
              />
            </div>
          </div>

          {/* An existing user gets the full address book (the single fieldset
              only ever edited users.address_id, which is now just the DEFAULT
              entry of that book). During creation there is no user id to hang
              addresses off yet, so the plain fieldset still collects the first
              one and the book takes over on the next open. */}
          {user?.id ? (
            <AddressBook userId={user.id} />
          ) : (
            <>
              <AddressFieldset
                nameObject="address"
                isRC={isRC}
                layout="paired"
                bare
              />
              <PendingAddresses
                addresses={extraAddresses}
                setAddresses={setExtraAddresses}
                isRC={isRC}
              />
            </>
          )}

          <div className="overview-form__interests">
            <FieldGroup
              bare
              label="Interests"
              name="interest_categories"
              type="multiselect"
              options={categoryOptions}
              className="overview-form__interests-field rc-create-multiselect"
            />
          </div>

          {values.type === "client" && (
            <div className="overview-form__others">
              <div className="overview-form__section-label">Others</div>
              <div className="row overview-form__row">
                <div className="col-md-6">
                  <FieldGroup
                    bare
                    placeholder="Investor type"
                    name="investor_type"
                    type="select"
                    options={DataHelper.investor_type}
                  />
                </div>
              </div>
            </div>
          )}
          </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overview-form">
      <div className="overview-form__fields">
        <div className="row overview-form__row">
          <div className="col-md-6">
            <FieldGroup
              placeholder="User type"
              type="select"
              options={DataHelper.userType}
              name="type"
              disabled={isProfile}
            />
          </div>
          <div className="col-md-6">
            <FieldGroup
              placeholder="Gender"
              name="gender"
              type="select"
              options={GENDER_OPTIONS}
              disabled={isRC}
              required={!isRC}
            />
          </div>
        </div>

        <div className="row overview-form__row">
          <div className="col-md-6">
            <FieldGroup placeholder="First Name" name="first_name" />
            <FieldGroup placeholder="Last Name" name="last_name" />
            <FieldGroup
              placeholder="Date of birth (DD/MM/YYYY)"
              name="birth_date"
              type="masked"
              mask="99/99/9999"
              disabled={isRC}
            />
            <FieldGroup
              placeholder="Nationality"
              name="nationality_id"
              type="nationality"
              disabled={isRC}
            />
            {!isProfile && (
              <>
                <FieldGroup
                  placeholder="Primary Email"
                  type="email"
                  name="email"
                  readOnly={isClaimed}
                  disabled={isClaimed}
                />
                <FieldGroup
                  placeholder="Secondary Email"
                  type="email"
                  name="secondary_email"
                />
              </>
            )}
          </div>

          <div className="col-md-6">
            {/* `rcCountrySelect` is gated on `isProfile` only — the /profile
                call site is the single place `isProfile` is ever true (see
                views/Profile/EditView.jsx), so the Users detail panel keeps the
                native country <select> it renders today. It brings /profile in
                line with the create branch above, which already uses the RC
                picker; the native select also emits ~250 <option> elements per
                phone field that theme.css paints in Calibri. */}
            <FieldGroup
              placeholder="Mobile"
              type="phone"
              name="phone"
              tooltip="Mobile"
              rcCountrySelect={isProfile}
            />
            <FieldGroup
              placeholder="Other Mobile"
              type="phone"
              name="other_phone"
              tooltip="Mobile"
              disabled={isRC}
              rcCountrySelect={isProfile}
            />
            <ReactTooltip />
            {isProfile && (
              <>
                <FieldGroup
                  placeholder="Primary Email"
                  type="email"
                  name="email"
                />
                <FieldGroup
                  placeholder="Secondary Email"
                  type="email"
                  name="secondary_email"
                />
              </>
            )}
            {/* Same rule as the paired layout above: book for a saved user,
                plain fieldset while the user is still being created. */}
            {!isProfile && (user?.id
              ? <AddressBook userId={user.id} />
              : (
                <>
                  <AddressFieldset nameObject="address" isRC={isRC} />
                  <PendingAddresses
                    addresses={extraAddresses}
                    setAddresses={setExtraAddresses}
                    isRC={isRC}
                  />
                </>
              ))}
          </div>
        </div>

        <div className="overview-form__interests">
          <FieldGroup
            label="Interests"
            name="interest_categories"
            type="multiselect"
            options={Window.CATEGORIES}
            className="overview-form__interests-field"
          />
        </div>

        {values.type === "client" && !isProfile && (
          <div className="overview-form__others">
            <div className="overview-form__section-label">Others</div>
            <div className="row overview-form__row">
              <div className="col-md-6">
                <FieldGroup
                  placeholder="Investor type"
                  name="investor_type"
                  type="select"
                  options={DataHelper.investor_type}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewForm;
